import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import {
    Plus,
    Trash2,
    Edit2,
    X,
    Play,
    Globe,
    AlertCircle,
    Loader2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { PermissionsGate } from '../../components/common/PermissionsGate';

interface PostbackConfig {
    id: string;
    url: string;
    event_type: 'signup' | 'purchase' | 'upsell' | 'all';
    method: 'GET' | 'POST';
    active: boolean;
    is_global?: boolean;
}

export default function GlobalPostbacks() {
    const { } = useAuth();
    const [configs, setConfigs] = useState<PostbackConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingConfig, setEditingConfig] = useState<PostbackConfig | null>(null);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
    const [testingId, setTestingId] = useState<string | null>(null);

    // Form state
    const [formData, setFormData] = useState<Partial<PostbackConfig>>({
        url: '',
        event_type: 'all',
        method: 'GET',
        active: true,
        is_global: true
    });

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async () => {
        setLoading(true);
        try {
            const managePostbackConfig = httpsCallable(functions, 'managePostbackConfig');
            // We need to implement a way to list global configs. 
            // Currently managePostbackConfig lists user's own configs.
            // But we modified it to check 'is_global' on create/update.
            // For listing, we probably need to pass a flag or check permissions?
            // Wait, logic in affiliate.ts for 'list' uses `configsRef.orderBy...`.
            // In my update to affiliate.ts, I set `configsRef` based on `config?.is_global`.
            // BUT, `data.config` might be undefined for `list` action if we don't pass it.
            // I need to update my call here to pass { is_global: true } in config object even for list action?
            // Let's check affiliate.ts changes...
            // Yes: `const { action, config } = data; ... if (config?.is_global) ...`
            // So for list action I must pass config: { is_global: true }

            const result = await managePostbackConfig({
                action: 'list',
                config: { is_global: true }
            });
            const data = result.data as any;
            if (data.success) {
                setConfigs(data.configs);
            }
        } catch (error) {
            console.error('Error fetching configs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!formData.url) return;

        try {
            const managePostbackConfig = httpsCallable(functions, 'managePostbackConfig');
            const action = editingConfig ? 'update' : 'create';

            await managePostbackConfig({
                action,
                config: {
                    ...formData,
                    id: editingConfig?.id,
                    is_global: true // Ensure strictly global
                }
            });

            setShowModal(false);
            setEditingConfig(null);
            setFormData({ url: '', event_type: 'all', method: 'GET', active: true, is_global: true });
            fetchConfigs();
        } catch (error) {
            console.error('Error saving config:', error);
            alert('Failed to save configuration');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this global postback?')) return;

        try {
            const managePostbackConfig = httpsCallable(functions, 'managePostbackConfig');
            await managePostbackConfig({
                action: 'delete',
                config: { id, is_global: true } // Must find the right collection
            });
            fetchConfigs();
        } catch (error) {
            console.error('Error deleting config:', error);
            alert('Failed to delete configuration');
        }
    };

    const handleTest = async (config: PostbackConfig) => {
        setTestingId(config.id);
        setTestResult(null);
        try {
            const managePostbackConfig = httpsCallable(functions, 'managePostbackConfig');
            await managePostbackConfig({
                action: 'test',
                config: { url: config.url, method: config.method, is_global: true }
            });
            // Result handling same as before
            const result = await managePostbackConfig({
                action: 'test',
                config: { url: config.url, method: config.method, is_global: true }
            });
            const data = result.data as any;
            setTestResult({
                success: data.success,
                message: data.message
            });
        } catch (error: any) {
            setTestResult({
                success: false,
                message: error.message || 'Test failed'
            });
        } finally {
            setTestingId(null);
        }
    };

    const openEdit = (config: PostbackConfig) => {
        setEditingConfig(config);
        setFormData({
            url: config.url,
            event_type: config.event_type,
            method: config.method,
            active: config.active,
            is_global: true
        });
        setShowModal(true);
    };

    const openCreate = () => {
        setEditingConfig(null);
        setFormData({ url: '', event_type: 'all', method: 'GET', active: true, is_global: true });
        setShowModal(true);
    };

    return (
        <PermissionsGate permission="manage_settings">
            <div className="space-y-6 animate-fade-in p-8 max-w-7xl mx-auto">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-linear-to-r from-purple-400 to-pink-600 flex items-center gap-3">
                            <Globe className="text-purple-500" />
                            Global Postbacks
                        </h1>
                        <p className="text-gray-400 mt-1">Configure postbacks that fire for ALL conversions across the platform</p>
                    </div>
                    <button
                        onClick={openCreate}
                        className="btn-primary flex items-center gap-2 bg-linear-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    >
                        <Plus size={20} />
                        New Global Postback
                    </button>
                </div>

                {/* Test Result Alert */}
                {testResult && (
                    <div className={`p-4 rounded-lg border flex items-start gap-3 ${testResult.success ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
                        }`}>
                        <AlertCircle size={20} className="mt-0.5" />
                        <div>
                            <p className="font-medium">{testResult.success ? 'Test Successful' : 'Test Failed'}</p>
                            <p className="text-sm opacity-90">{testResult.message}</p>
                        </div>
                        <button onClick={() => setTestResult(null)} className="ml-auto hover:text-white">
                            <X size={16} />
                        </button>
                    </div>
                )}

                {/* Configs List */}
                <div className="bg-[#1a1b26] rounded-xl border border-white/5 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-white/5">
                                <tr className="border-b border-white/10">
                                    <th className="py-4 px-6 text-gray-400 font-medium text-sm uppercase tracking-wider">Method</th>
                                    <th className="py-4 px-6 text-gray-400 font-medium text-sm uppercase tracking-wider w-full">URL</th>
                                    <th className="py-4 px-6 text-gray-400 font-medium text-sm uppercase tracking-wider">Event</th>
                                    <th className="py-4 px-6 text-gray-400 font-medium text-sm uppercase tracking-wider">Status</th>
                                    <th className="py-4 px-6 text-gray-400 font-medium text-sm uppercase tracking-wider text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="py-12 text-center">
                                            <Loader2 size={32} className="mx-auto animate-spin text-purple-500" />
                                        </td>
                                    </tr>
                                ) : configs.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="py-12 text-center text-gray-500">
                                            No global postbacks configured yet.
                                        </td>
                                    </tr>
                                ) : (
                                    configs.map(config => (
                                        <tr key={config.id} className="hover:bg-white/5 transition-colors">
                                            <td className="py-4 px-6">
                                                <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${config.method === 'POST' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                                                    }`}>
                                                    {config.method}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="font-mono text-sm text-gray-300 break-all max-w-lg">
                                                    {config.url}
                                                </div>
                                            </td>
                                            <td className="py-4 px-6">
                                                <span className="capitalize text-gray-300 px-2 py-1 rounded bg-white/5 text-sm">{config.event_type}</span>
                                            </td>
                                            <td className="py-4 px-6">
                                                {config.active ? (
                                                    <span className="flex items-center gap-1.5 text-emerald-400 text-sm font-medium">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]" />
                                                        Active
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1.5 text-gray-500 text-sm font-medium">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                                                        Inactive
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-4 px-6 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleTest(config)}
                                                        disabled={testingId === config.id}
                                                        title="Test Fire"
                                                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                                                    >
                                                        {testingId === config.id ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                                                    </button>
                                                    <button
                                                        onClick={() => openEdit(config)}
                                                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(config.id)}
                                                        className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Edit/Create Modal */}
                {showModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                        <div className="bg-[#1a1b26] border border-white/10 rounded-xl w-full max-w-lg relative animate-slide-up shadow-2xl">
                            <button
                                onClick={() => setShowModal(false)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-white"
                            >
                                <X size={20} />
                            </button>

                            <div className="p-6 border-b border-white/10">
                                <h2 className="text-xl font-bold text-white">
                                    {editingConfig ? 'Edit Global Postback' : 'New Global Postback'}
                                </h2>
                            </div>

                            <div className="p-6 space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1.5">Postback URL</label>
                                    <input
                                        type="text"
                                        value={formData.url}
                                        onChange={e => setFormData({ ...formData, url: e.target.value })}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all"
                                        placeholder="https://tracker.com/pb?click={click_id}&payout={payout}"
                                    />
                                    <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                                        Available placeholders: <span className="text-purple-400 font-mono">{`{click_id}, {amount}, {payout}, {currency}, {conversion_id}, {event_type}, {sub_id}, {source_id}`}</span>
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-1.5">Method</label>
                                        <select
                                            value={formData.method}
                                            onChange={e => setFormData({ ...formData, method: e.target.value as any })}
                                            className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all appearance-none"
                                        >
                                            <option value="GET">GET</option>
                                            <option value="POST">POST</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-1.5">Event Type</label>
                                        <select
                                            value={formData.event_type}
                                            onChange={e => setFormData({ ...formData, event_type: e.target.value as any })}
                                            className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all appearance-none"
                                        >
                                            <option value="all">All Events</option>
                                            <option value="purchase">Purchase (Default)</option>
                                            <option value="signup">Lead / Signup</option>
                                            <option value="upsell">Upsell</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 pt-2">
                                    <label className="flex items-center gap-2 text-gray-300 cursor-pointer select-none group">
                                        <input
                                            type="checkbox"
                                            checked={formData.active}
                                            onChange={e => setFormData({ ...formData, active: e.target.checked })}
                                            className="rounded border-gray-600 bg-black/50 text-purple-500 focus:ring-purple-500 w-5 h-5 transition-colors"
                                        />
                                        <span className="group-hover:text-white transition-colors">Active</span>
                                    </label>
                                </div>

                                <div className="pt-4 flex justify-end gap-3 border-t border-white/10 mt-6">
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="px-4 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        className="px-6 py-2 bg-linear-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-900/20 transition-all font-medium"
                                        disabled={!formData.url}
                                    >
                                        Save Config
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </PermissionsGate>
    );
}
